import React, { useEffect } from 'react';
import {
  MapPin,
  Database,
  Download,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  ArrowRight,
  Target
} from 'lucide-react';
import './Landing.css';
import api from './api';
import Apresentacao from './Apresentacao';

export default function Landing({ onLogin, isLoggedIn }) {
  const mapContainerRef = React.useRef(null);

  useEffect(() => {
    let mapInstance = null;
    let markersGroup = null;

    const initMap = async () => {
      if (!mapContainerRef.current) return;
      if (!window.L) return;
      
      // Previne re-inicialização (erro comum em React 18 StrictMode)
      if (mapContainerRef.current._leaflet_id) {
        return;
      }

      try {
        // Limites do Brasil para travar o mapa
        const brazilBounds = [
          [-34.0, -74.0], // Sudoeste
          [5.5, -34.0]    // Nordeste
        ];

        // Inicializa o mapa de forma síncrona travado no Brasil
        mapInstance = window.L.map(mapContainerRef.current, {
          zoomControl: true,
          attributionControl: false,
          maxBounds: brazilBounds,
          maxBoundsViscosity: 1.0,
          minZoom: 4
        }).setView([-14.235, -51.925], 4);

        // Tile layer dark-themed
        window.L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
          maxZoom: 19
        }).addTo(mapInstance);

        // Busca dados da API
        const res = await api.get('/public/leads-locations');
        const locations = Array.isArray(res.data) ? res.data : [];

        if (locations.length > 0) {
          // Extrai apenas pontos válidos com intensidade padrão 1
          const heatData = locations
            .filter(loc => loc.lat && loc.lng)
            .map(loc => [loc.lat, loc.lng, 1]);
            
          if (window.L.heatLayer) {
            window.L.heatLayer(heatData, {
              radius: 12,
              blur: 20,
              maxZoom: 10,
              max: 1.0,
              gradient: {
                0.2: '#002244',
                0.4: '#0055ff',
                0.6: '#00f5c4',
                0.8: '#ffaa00',
                1.0: '#ff2200'
              }
            }).addTo(mapInstance);
          } else {
             // Fallback caso o plugin não tenha carregado
             markersGroup = window.L.layerGroup().addTo(mapInstance);
             locations.slice(0, 500).forEach(loc => {
               if (loc.lat && loc.lng) {
                 window.L.circleMarker([loc.lat, loc.lng], { radius: 2, color: '#00f5c4' }).addTo(markersGroup);
               }
             });
          }
        }
      } catch (e) {
        console.warn("Erro ao iniciar o mapa público na landing page:", e);
      }
    };

    initMap();

    return () => {
      if (mapInstance) {
        try { mapInstance.remove(); } catch(err) {}
      }
    };
  }, []);

  // Simple scroll to section
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="tl-shell">
      {/* HEADER */}
      <header className="tl-header">
        <div className="tl-container tl-nav">
          <a href="#" className="tl-logo">
            <Target className="tl-logo-icon" size={28} />
            LeadsZone Maps
          </a>
          <div className="tl-nav-links">
            <a href="#como-funciona" onClick={(e) => { e.preventDefault(); scrollTo('como-funciona'); }}>Como Funciona</a>
            <a href="#diferenciais" onClick={(e) => { e.preventDefault(); scrollTo('diferenciais'); }}>Diferenciais</a>
            <a href="#planos" onClick={(e) => { e.preventDefault(); scrollTo('planos'); }}>Planos</a>
            <a href="https://www.whatszone.app.br" target="_blank" rel="noreferrer" style={{ color: 'var(--tl-cyan)', fontWeight: 'bold', textDecoration: 'none' }}>🚀 WhatsZone</a>
            <button className="tl-btn-primary" onClick={() => onLogin && onLogin()} style={{ padding: '0.6rem 1.5rem' }}>
              {isLoggedIn ? 'Acessar Painel' : 'Entrar'} <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </header>

      {/* HERO SECTION */}
      <section className="tl-hero tl-section">
        <div className="tl-hero-bg"></div>
        <div className="tl-hero-grid"></div>
        <div className="tl-container" style={{ position: 'relative', zIndex: 10 }}>
          <h1 className="tl-title text-shimmer">Acesse a Maior Base de Leads.<br />Exporte na hora.</h1>
          <p className="tl-subtitle">
            Filtre nossa base viva por região e segmento. Escolha os leads exatos que você precisa, adicione à sua carteira, pague com segurança e exporte os dados estruturados imediatamente para sua equipe de vendas.
          </p>
          
          <div className="tl-hero-actions">
            <button className="tl-btn-primary" onClick={() => onLogin && onLogin()}>
              {isLoggedIn ? 'Acessar o Painel' : 'Acessar a Base'} <ArrowRight size={18} />
            </button>
            <button className="tl-btn-secondary" onClick={() => scrollTo('apresentacao')}>
              Ver Apresentação Oficial 🎥
            </button>
          </div>

          {/* Abstract Interface Preview */}
          <div className="tl-hero-preview">
            <div className="tl-preview-header">
              <div className="tl-dot"></div>
              <div className="tl-dot"></div>
              <div className="tl-dot"></div>
            </div>
            <div className="tl-hero-preview-content">
              <div className="tl-preview-card">
                <span style={{ color: '#ef4444', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '2px' }}>Listas Genéricas</span>
                <div style={{ marginTop: '0.5rem', fontSize: '1rem', color: 'rgba(255,255,255,0.6)' }}>
                  <del>Empresa Fechada LTDA</del><br/>
                  <del>(11) 3333-0000</del> (Fixo)<br/>
                  <del>contato@contabilidade.com</del>
                </div>
              </div>
              <div className="tl-preview-card tl-preview-card-alt">
                <span style={{ color: '#00f5c4', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '2px', fontWeight: 'bold' }}>Tecnologia LeadsZone</span>
                <div style={{ marginTop: '0.5rem', fontSize: '1.1rem', color: '#fff', fontWeight: 'bold' }}>
                  Clínica Odonto Prime <span style={{fontSize:'0.8rem'}}>⭐ 4.8</span><br/>
                  <span style={{color: '#00f5c4'}}>(11) 99999-9999</span> (WhatsApp)<br/>
                  dr.joao@clinica.com
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAP DE DISPERSÃO PÚBLICO */}
      <section className="tl-section" style={{ background: '#020818', padding: '4rem 0 2rem 0', position: 'relative', zIndex: 10, borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
        <div className="tl-container" style={{ textAlign: 'center' }}>
          <h2 className="tl-section-title" style={{ marginBottom: '0.5rem', color: '#fff' }}>🌐 Cobertura Nacional de Leads</h2>
          <p className="tl-section-desc" style={{ marginBottom: '2.5rem', color: 'var(--tl-text-muted)' }}>
            Nossa base de leads vivos geolocalizados cobrindo todo o Brasil.
          </p>
          
          <div className="glass card" style={{ padding: '1rem', background: 'rgba(8, 13, 28, 0.76)', border: '1px solid rgba(116, 242, 255, 0.08)', borderRadius: '20px', maxWidth: '1000px', margin: '0 auto' }}>
            <div 
              ref={mapContainerRef} 
              style={{ 
                height: '450px', 
                borderRadius: '16px', 
                border: '1px solid rgba(116, 242, 255, 0.12)', 
                overflow: 'hidden',
                background: '#050816'
              }} 
            />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS SECTION */}
      <section id="como-funciona" className="tl-section tl-section-alt">
        <div className="tl-container">
          <h2 className="tl-section-title">Busque, Selecione e Exporte</h2>
          <p className="tl-section-desc">O fluxo mais rápido para abastecer o seu CRM. Você tem controle total sobre quais leads comprar.</p>
          
          <div className="tl-features-grid">
            <div className="tl-feature-card">
              <div className="tl-feature-icon">
                <MapPin />
              </div>
              <h3>Busca Cirúrgica</h3>
              <p>Acesse um painel inteligente e filtre leads por estado, fontes e segmento comercial. Nossa base já foi mapeada com alta precisão.</p>
            </div>
            
            <div className="tl-feature-card">
              <div className="tl-feature-icon">
                <Database />
              </div>
              <h3>Cruzamento de Inteligência (Google & LinkedIn)</h3>
              <p>Nossa inteligência cruza todos os dados captados no Google, sites corporativos e registros comerciais diretamente com os perfis do LinkedIn para obter e validar os contatos dos reais tomadores de decisão da empresa.</p>
            </div>
            
            <div className="tl-feature-card">
              <div className="tl-feature-icon">
                <Download />
              </div>
              <h3>Compra e Exportação Imediata</h3>
              <p>Adicione os leads na sua carteira, realize o pagamento com 100% de segurança via <strong>Stripe</strong> e o download do CSV é liberado na mesma hora. Sem aguardar dias por entregas.</p>
            </div>
          </div>
          
          <div style={{ textAlign: 'center', marginTop: '3rem' }}>
            <a href="/apresentacao" target="_blank" rel="noopener noreferrer" className="tl-btn-secondary" style={{ display: 'inline-flex', padding: '1rem 2rem', fontSize: '1.1rem', textDecoration: 'none' }}>
              Veja Nossa Tecnologia Por Dentro <ArrowRight size={18} style={{ marginLeft: '10px' }} />
            </a>
          </div>
        </div>
      </section>

      {/* COMPARISON SECTION */}
      <section id="diferenciais" className="tl-section">
        <div className="tl-container">
          <h2 className="tl-section-title">O Padrão Ouro em Leads</h2>
          <p className="tl-section-desc">Veja a diferença entre depender de listas frias obsoletas e extrair inteligência em tempo real direto da fonte.</p>

          <div className="tl-comparison">
            {/* The Old Way */}
            <div className="tl-comp-card tl-comp-them">
              <h3 style={{ color: '#ff4d4d', fontSize: '1.5rem', marginBottom: '0.5rem' }}>Bancos Genéricos</h3>
              <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '2rem' }}>A forma antiga de prospectar.</p>
              <ul className="tl-comp-list">
                <li><XCircle color="#ff4d4d" /> <span>Listas recicladas e desatualizadas.</span></li>
                <li><XCircle color="#ff4d4d" /> <span>Contatos frequentemente caem em contabilidades.</span></li>
                <li><XCircle color="#ff4d4d" /> <span>Zero adequação geográfica fina (apenas "Estado/Cidade").</span></li>
                <li><XCircle color="#ff4d4d" /> <span>Requer horas de limpeza no Excel antes do uso.</span></li>
              </ul>
            </div>

            {/* LeadsZone Maps Way */}
            <div className="tl-comp-card tl-comp-us">
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(0,240,255,0.1)', color: 'var(--tl-cyan)', padding: '5px 12px', borderRadius: '50px', fontSize: '0.8rem', fontWeight: 'bold', marginBottom: '1rem' }}>
                <ShieldCheck size={16} /> COMPRA GARANTIDA
              </div>
              <h3 style={{ color: 'var(--tl-cyan)', fontSize: '2rem', marginBottom: '0.5rem' }}>LeadsZone Maps</h3>
              <p style={{ color: 'var(--tl-text-muted)', marginBottom: '2rem' }}>A precisão tecnológica que seu time de vendas exige.</p>
              <ul className="tl-comp-list">
                <li><CheckCircle2 color="var(--tl-cyan)" /> <span>Base exclusiva, geolocalizada e atualizada continuamente em tempo real.</span></li>
                <li><CheckCircle2 color="var(--tl-cyan)" /> <span><strong>Cruzamento de Dados de CNPJ:</strong> Enriquecemos os leads com o CNPJ oficial da Receita Federal, quadro de sócios administradores (QSA) e capital social para você ir direto ao decisor.</span></li>
                <li><CheckCircle2 color="var(--tl-cyan)" /> <span><strong>Mapa Interativo Integrado:</strong> Visualize espacialmente onde os leads estão localizados com nosso mapa dinâmico de dispersão.</span></li>
                <li><CheckCircle2 color="var(--tl-cyan)" /> <span>Filtros avançados e seleção de pacote minuciosa direto no painel.</span></li>
                <li><CheckCircle2 color="var(--tl-cyan)" /> <span>Entregue higienizado e pronto para importar no PipeDrive/RD Station.</span></li>
              </ul>
              
              <button className="tl-btn-primary" onClick={() => onLogin && onLogin()} style={{ width: '100%', justifyContent: 'center', marginTop: '2rem' }}>
                {isLoggedIn ? 'Ir para o Painel' : 'Quero essa precisão'} <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING SECTION */}
      <section id="planos" className="tl-section tl-section-alt">
        <div className="tl-container">
          <h2 className="tl-section-title">Preços Claros e Transparentes</h2>
          <p className="tl-section-desc">Pague apenas pela quantidade de leads que escolher. Sem mensalidades, sem custos ocultos.</p>

          <div className="tl-pricing-grid">
            {/* Card 1: Starter */}
            <div className="tl-pricing-card">
              <span className="tl-pricing-badge">Starter</span>
              <div className="tl-pricing-price">
                <span className="tl-price-symbol">R$</span>
                <span className="tl-price-amount">3,00</span>
                <span className="tl-price-per">/ lead</span>
              </div>
              <p className="tl-pricing-range">De 25 a 349 leads selecionados</p>
              <div className="tl-pricing-divider"></div>
              <ul className="tl-pricing-features">
                <li><CheckCircle2 color="var(--tl-cyan)" size={18} /> <span>Acesso total à base</span></li>
                <li><CheckCircle2 color="var(--tl-cyan)" size={18} /> <span>Filtros geográficos finos</span></li>
                <li><CheckCircle2 color="var(--tl-cyan)" size={18} /> <span>Contatos higienizados</span></li>
                <li><CheckCircle2 color="var(--tl-cyan)" size={18} /> <span>Download instantâneo</span></li>
              </ul>
              <button className="tl-btn-secondary" onClick={() => onLogin && onLogin()} style={{ width: '100%', justifyContent: 'center', marginTop: 'auto' }}>
                {isLoggedIn ? 'Acessar Painel' : 'Começar Agora'}
              </button>
            </div>

            {/* Card 2: Pro */}
            <div className="tl-pricing-card tl-pricing-card--popular">
              <div className="tl-pricing-popular-tag">🔥 O MAIS VENDIDO</div>
              <span className="tl-pricing-badge" style={{ color: '#00f5c4' }}>PRO</span>
              <div className="tl-pricing-price">
                <span className="tl-price-symbol">R$</span>
                <span className="tl-price-amount">2,50</span>
                <span className="tl-price-per">/ lead</span>
              </div>
              <div className="tl-price-discount-badge">ECONOMIZE 16%</div>
              <p className="tl-pricing-range" style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>De 350 a 999 leads selecionados</p>
              <div className="tl-pricing-divider"></div>
              <ul className="tl-pricing-features">
                <li><CheckCircle2 color="#00f5c4" size={18} /> <span><strong>Todos os recursos Starter</strong></span></li>
                <li><CheckCircle2 color="#00f5c4" size={18} /> <span>Preço de lead reduzido</span></li>
                <li><CheckCircle2 color="#00f5c4" size={18} /> <span>Pronto para importar no CRM</span></li>
                <li><CheckCircle2 color="#00f5c4" size={18} /> <span><strong>Suporte Prioritário no WhatsApp</strong></span></li>
              </ul>
              <button className="tl-btn-primary" onClick={() => onLogin && onLogin()} style={{ width: '100%', justifyContent: 'center', marginTop: 'auto', background: '#00f5c4', color: '#000', fontWeight: '800', boxShadow: '0 0 25px rgba(0, 245, 196, 0.5)', border: 'none' }}>
                {isLoggedIn ? 'Acessar Painel' : 'Garantir Plano Pro'} <ArrowRight size={18} />
              </button>
            </div>

            {/* Card 3: VIP */}
            <div className="tl-pricing-card">
              <span className="tl-pricing-badge" style={{ color: 'var(--tl-purple)' }}>VIP</span>
              <div className="tl-pricing-price">
                <span className="tl-price-symbol">R$</span>
                <span className="tl-price-amount">2,00</span>
                <span className="tl-price-per">/ lead</span>
              </div>
              <p className="tl-pricing-range">Acima de 1000 leads (Economia de 33%)</p>
              <div className="tl-pricing-divider"></div>
              <ul className="tl-pricing-features">
                <li><CheckCircle2 color="var(--tl-purple)" size={18} /> <span>Todos os recursos Pro</span></li>
                <li><CheckCircle2 color="var(--tl-purple)" size={18} /> <span>Máxima economia por volume</span></li>
                <li><CheckCircle2 color="var(--tl-purple)" size={18} /> <span>Acesso a novas extrações</span></li>
                <li><CheckCircle2 color="var(--tl-purple)" size={18} /> <span>Gerente de contas dedicado</span></li>
              </ul>
              <button className="tl-btn-secondary" onClick={() => onLogin && onLogin()} style={{ width: '100%', justifyContent: 'center', marginTop: 'auto', borderColor: 'var(--tl-purple)' }}>
                {isLoggedIn ? 'Acessar Painel' : 'Selecionar VIP'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* APRESENTAÇÃO EMBARCADA */}
      <div id="apresentacao" style={{ position: 'relative', zIndex: 10, background: '#020818' }}>
        <Apresentacao embedded={true} />
      </div>

      {/* LEGAL & LGPD SECTION */}
      <section className="tl-section" style={{ background: '#050c1e', borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)', padding: '4rem 0' }}>
        <div className="tl-container" style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
          <h2 className="tl-section-title" style={{ fontSize: '1.8rem', marginBottom: '1rem' }}>
            🛡️ Segurança Jurídica e Conformidade com a LGPD
          </h2>
          <p style={{ color: 'var(--tl-text-muted)', fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '2rem' }}>
            Nossa plataforma opera em total alinhamento com as diretrizes da <strong>Lei Geral de Proteção de Dados (Lei 13.709/2018)</strong>, oferecendo total conformidade e transparência para a prospecção B2B do seu negócio.
          </p>

          <div className="tl-lgpd-grid">
            <div className="tl-lgpd-card">
              <h4 style={{ color: 'var(--tl-cyan)', margin: '0 0 10px 0', fontSize: '1rem', fontWeight: 'bold' }}>
                Dados Manifestamente Públicos
              </h4>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', lineHeight: '1.5', margin: 0 }}>
                Em conformidade com o <strong>Artigo 7º, § 4º da LGPD</strong>, o consentimento do titular é dispensado para o tratamento de dados que tenham sido tornados manifestamente públicos pelo próprio titular (como telefones comerciais, e-mails e localizações cadastrados no Google Maps por empresas para fins de atração de clientes).
              </p>
            </div>

            <div className="tl-lgpd-card">
              <h4 style={{ color: 'var(--tl-cyan)', margin: '0 0 10px 0', fontSize: '1rem', fontWeight: 'bold' }}>
                Legítimo Interesse Comercial
              </h4>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', lineHeight: '1.5', margin: 0 }}>
                O processamento e a prospecção ativa de contatos comerciais B2B (de empresas e profissionais liberais) são amplamente respaldados no <strong>Artigo 7º, IX da LGPD</strong>, que confere legalidade ao tratamento sob a base de legítimo interesse para promoção comercial e desenvolvimento de novos negócios.
              </p>
            </div>
          </div>

          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', marginTop: '2rem', lineHeight: '1.5' }}>
            💡 <strong>Dica de Conformidade:</strong> Ao realizar contatos comerciais ativos, sempre inclua um canal claro de descadastramento (Opt-Out). Respeite imediatamente a solicitação de remoção caso o lead se oponha ao recebimento de mensagens.
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="tl-footer">
        <div className="tl-container">
          <div className="tl-logo" style={{ justifyContent: 'center' }}>
            <Target className="tl-logo-icon" size={24} /> LeadsZone Maps
          </div>
          <p>© 2026. Todos os direitos reservados. Software em conformidade com a LGPD e termos do Google.</p>
        </div>
      </footer>
    </div>
  );
}
